/**
 * Parcours de lecture : l'ordre des candidats de la dernière liste consultée
 * (demande du 2026-09-19 : « passer au candidat suivant ou précédent via deux
 * boutons flèches »). La fiche ne sait pas d'où l'on vient — et l'ordre dépend
 * des filtres et du tri posés sur la liste, que la fiche ne peut pas
 * recalculer. La liste écrit donc son ordre en partant, la fiche le relit.
 *
 * `sessionStorage`, comme les filtres de liste : la mémoire meurt avec
 * l'onglet et s'efface à la déconnexion (`oublierParcours`). Rien de personnel
 * n'y entre — des identifiants de candidature, l'adresse de la liste et son
 * intitulé. Tout accès est protégé : sans stockage (navigation privée), la
 * fiche s'affiche simplement sans flèches.
 */
const CLE = 'rh-app:parcours';

/** Au-delà, la mémoire ne sert plus à rien et pèse pour rien. */
const MAX = 500;

export interface Parcours {
  /** Adresse de la liste, filtres compris : c'est elle que « Retour » rouvre. */
  retour: string;
  /** Nom de la liste, dit à voix haute par les flèches (« suivant dans Vendeur… »). */
  libelle: string;
  /** Identifiants des candidatures, dans l'ordre affiché. */
  ids: string[];
}

export interface Voisins {
  precedent: string | null;
  suivant: string | null;
  /** Rang du candidat courant, à partir de 1. */
  rang: number;
  total: number;
  libelle: string;
  retour: string;
}

export function ecrireParcours(parcours: Parcours): void {
  try {
    if (parcours.ids.length === 0) {
      sessionStorage.removeItem(CLE);
      return;
    }
    sessionStorage.setItem(
      CLE,
      JSON.stringify({ ...parcours, ids: parcours.ids.slice(0, MAX) }),
    );
  } catch {
    // Stockage refusé (navigation privée, quota) : on se passe de parcours.
  }
}

export function lireParcours(): Parcours | null {
  try {
    const brut = sessionStorage.getItem(CLE);
    if (!brut) return null;
    const lu: unknown = JSON.parse(brut);
    if (!lu || typeof lu !== 'object') return null;
    const { retour, libelle, ids } = lu as Partial<Parcours>;
    if (!Array.isArray(ids) || !ids.every((id) => typeof id === 'string')) return null;
    return {
      retour: typeof retour === 'string' ? retour : '',
      libelle: typeof libelle === 'string' ? libelle : '',
      ids: ids.slice(0, MAX),
    };
  } catch {
    return null;
  }
}

/**
 * Voisins de cette candidature dans le dernier parcours, ou `null` : fiche
 * ouverte par son adresse, venue d'ailleurs, ou liste d'un seul candidat — il
 * n'y a alors nulle part où aller, et les flèches ne s'affichent pas.
 */
export function voisins(id: string): Voisins | null {
  const parcours = lireParcours();
  if (!parcours) return null;
  const rang = parcours.ids.indexOf(id);
  if (rang < 0 || parcours.ids.length < 2) return null;
  return {
    precedent: rang > 0 ? parcours.ids[rang - 1]! : null,
    suivant: rang < parcours.ids.length - 1 ? parcours.ids[rang + 1]! : null,
    rang: rang + 1,
    total: parcours.ids.length,
    libelle: parcours.libelle,
    retour: parcours.retour,
  };
}

/** Fin de session : plus aucun parcours gardé. */
export function oublierParcours(): void {
  try {
    sessionStorage.removeItem(CLE);
  } catch {
    // Rien à faire : sans stockage, il n'y avait rien à oublier.
  }
}
